/**
 * Labstep
 *
 * @module components/ShareLink/Info
 * @desc Copy a ShareLink
 */

import { useHasAccessGroup } from 'labstep-web/components/Entity/Can/hooks';
import { GroupActionSetDefaultUserGroupType } from 'labstep-web/components/Group/Action/SetDefaultUserGroupType';
import { ShareLinkActionReCreateContainer as ShareLinkActionReCreate } from 'labstep-web/components/ShareLink/Action/ReCreate';
import { ShareLinkActionTogglePermissions } from 'labstep-web/components/ShareLink/Action/TogglePermissions';
import { ActionCopy } from 'labstep-web/core/Action/Copy';
import type {
  PermissionActions,
  ShareLinkParent,
} from 'labstep-web/models';
import { Group } from 'labstep-web/models/group.model';
import { navigation } from 'labstep-web/services/navigation';
import type React from 'react';
import { useState } from 'react';
import { Input } from 'semantic-ui-react';
import styles from './styles.module.scss';
import type { IShareLinkInfoProps } from './types';

export const canEdit = (parentEntity: ShareLinkParent) => {
  if (parentEntity.entityName === Group.entityName) {
    return useHasAccessGroup('owner', parentEntity.id);
  }

  return (
    parentEntity.allowed_actions.includes('*') ||
    parentEntity.allowed_actions.includes(
      `${parentEntity.entityName}:edit` as PermissionActions,
    )
  );
};

export const ShareLinkInfo: React.FC<IShareLinkInfoProps> = ({
  shareLink,
  parentEntity,
  header,
}) => {
  const [activeShareLink, setActiveShareLink] = useState(shareLink);
  const link =
    activeShareLink &&
    navigation.get(
      'sharelink_show',
      { token: activeShareLink.token },
      true,
    );

  return (
    <>
      <span>{header || 'Share via a link'}</span>
      <div className={styles.container}>
        <div className={styles.input}>
          <Input fluid value={link} />
        </div>
        <div>
          <ShareLinkActionReCreate
            parentEntity={parentEntity}
            options={{
              onSuccess: ({ response }) => {
                if (!response) {
                  return;
                }
                setActiveShareLink(
                  response.entities.share_link[
                    response.result as any
                  ] as any,
                );
              },
            }}
          />
        </div>
        <ActionCopy
          text={link}
          actionComponentProps={{
            type: 'button',
            elementProps: { primary: true },
            text: 'Copy',
          }}
        />
        {canEdit(parentEntity) && (
          <div>
            {parentEntity.entityName === Group.entityName ? (
              <GroupActionSetDefaultUserGroupType
                group={parentEntity as Group}
              />
            ) : (
              <ShareLinkActionTogglePermissions
                shareLinkId={activeShareLink.id}
              />
            )}
          </div>
        )}
      </div>
    </>
  );
};
